import { useTranslation } from 'react-i18next'
import { getFullName, getStringWithFirstLetterInUpperCase } from 'src/utils/string-formatter.util'
import { twMerge } from 'src/utils/tw-merge.util'
import { CurecallButton, DialogueBubbleIcon, ECurecallSize, NoteIcon } from '@honestica/curecall-ui'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { TPatientForSearchBar } from './searchbar.type'
import { isCensoredBirthdate } from 'src/utils/date-formatter.util'
import { getBirthdateHighlightSegments } from './search-highlight.util'
import { HighlightedSegments, HighlightedText } from './highlighted-text.component'

export type Props = {
  searchResults: Array<TPatientForSearchBar>
  cursorIndex: number
  projectId: number
  clearField: VoidFunction
  searchValue: string
  resultsContainerRef: React.RefObject<HTMLDivElement | null>
}

const SearchbarResults = ({
  searchResults,
  cursorIndex,
  projectId,
  clearField,
  searchValue,
  resultsContainerRef,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()

  const openSMS = (patientId: number) => {
    navigate(`/${projectId}/medical/patients/${patientId}?isSmsModaleOpen=true`)
  }
  const openMedicalTeamComments = (patientId: number) => {
    navigate(`/${projectId}/medical/patients/${patientId}?isMedicalTeamCommentOpen=true`)
  }

  if (searchResults.length === 0 && searchValue.length !== 0)
    return (
      <span className="c-flex c-px-3 c-py-4 c-border c-border-gray-500 c-rounded-lg">
        {t('components.navigation.header.no-result')}
      </span>
    )

  if (searchResults.length > 0)
    return (
      <div
        className="c-max-h-[350px] c-overflow-auto c-py-2 c-border c-border-gray-500 c-rounded-lg"
        ref={resultsContainerRef}
      >
        {searchResults?.map((searchResult: TPatientForSearchBar, index) => (
          <div
            key={searchResult.id}
            className={twMerge(
              'hover:c-bg-grey-100 c-m-0 c-w-full c-flex',
              index === cursorIndex && 'c-bg-primary-200',
            )}
            tabIndex={0}
          >
            <LocalizedLink
              to={`/${projectId}/medical/patients/${searchResult.id}`}
              onClick={clearField}
              className="c-flex c-flex-col c-grow c-px-4 c-py-2.5 c-cursor-pointer hover:c-text-grey-700 [&_em]:c-text-secondary-800 [&_em]:c-not-italic"
            >
              <div className="c-text-sm c-font-bold c-flex c-gap-2 c-items-center ">
                <span>
                  {searchResult.remoteId ? (
                    <HighlightedText
                      value={searchResult.remoteId}
                      searchValue={searchValue}
                      positions={searchResult._matchesPosition.remoteId}
                    />
                  ) : (
                    searchResult.id
                  )}
                  {' : '}
                </span>
                <span>
                  <HighlightedText
                    value={searchResult.lastname}
                    searchValue={searchValue}
                    positions={searchResult._matchesPosition.lastname}
                    transformText={(text) => text.toUpperCase()}
                  />{' '}
                  <HighlightedText
                    value={getStringWithFirstLetterInUpperCase(searchResult.firstname ?? '')}
                    searchValue={searchValue}
                    positions={searchResult._matchesPosition.firstname}
                  />
                </span>
                <span>
                  {t('components.navigation.header.patient-gender-abbreviated', {
                    gender: searchResult.gender,
                  })}
                </span>
              </div>
              <div className="c-flex c-items-center c-gap-2">
                <span className="c-text-xs">
                  <HighlightedText
                    value={searchResult.phoneNationalFormat}
                    searchValue={searchValue}
                    positions={searchResult._matchesPosition.phoneNationalFormat}
                  />
                </span>
                <span className="c-text-xs c-text-grey-400">
                  {t('components.navigation.header.born', { gender: searchResult.gender })}{' '}
                  {isCensoredBirthdate(searchResult.birthdate) ? (
                    searchResult.birthdate
                  ) : (
                    <HighlightedSegments
                      segments={getBirthdateHighlightSegments(
                        searchResult.birthdate,
                        searchValue,
                        searchResult._matchesPosition.birthdate,
                      )}
                    />
                  )}
                </span>
              </div>
            </LocalizedLink>

            {searchResult.isArchived ? (
              <p className="c-italic c-m-0 c-flex c-items-center c-p-2">
                {t('components.navigation.header.archived-patient')}
              </p>
            ) : (
              <div className="c-flex c-gap-2 c-mx-4 c-items-center ">
                <CurecallButton
                  isGhostButton={true}
                  ariaLabelText={t('components.navigation.header.open-sms', {
                    name: getFullName({
                      firstname: searchResult.firstname,
                      lastname: searchResult.lastname,
                    }),
                  })}
                  iconType={<DialogueBubbleIcon />}
                  onClick={() => openSMS(+searchResult.id)}
                  size={ECurecallSize.MEDIUM}
                  className="tw-text-grey-700"
                />
                <CurecallButton
                  isGhostButton={true}
                  ariaLabelText={t('components.navigation.header.open-medical-team-comments', {
                    name: getFullName({
                      firstname: searchResult.firstname,
                      lastname: searchResult.lastname,
                    }),
                  })}
                  iconType={<NoteIcon />}
                  onClick={() => openMedicalTeamComments(+searchResult.id)}
                  size={ECurecallSize.MEDIUM}
                  className="tw-text-grey-700"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    )

  return null
}

export default SearchbarResults
